import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

const LOGO_URL =
  "https://storage.readdy-site.link/project_files/45b3cfca-45db-4e89-81dd-5e962535454e/ed46beda-b2e2-4e49-8fe8-982740950b38_-2026-03-26-2.12.17.png?v=503321e01f3f9a222c0dcb8439e9e8a5";

export default function Navbar() {
  const { t } = useTranslation("common");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.courseFlow"), path: "/#course-flow" },
    { label: t("nav.services"), path: "/services" },
    { label: t("nav.register"), path: "/register" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        transition: "background 0.3s, box-shadow 0.3s",
        background: scrolled ? "rgba(250,245,232,0.97)" : "transparent",
        boxShadow: scrolled ? "0 1px 12px rgba(44,95,79,0.08)" : "none",
      }}
    >
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between" style={{ height: 68 }}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src={LOGO_URL}
            alt="EachTeach"
            className="rounded-lg object-contain"
            style={{ width: 44, height: 44, background: "#F5EDD6" }}
          />
          <span
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              fontSize: 20,
              color: scrolled ? "#2C5F4F" : "#2C5F4F",
              letterSpacing: "0.01em",
              transition: "color 0.3s",
            }}
          >
            EachTeach
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            const isRegister = link.path === "/register";
            if (isRegister) {
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="whitespace-nowrap cursor-pointer"
                  style={{
                    padding: "8px 20px",
                    borderRadius: 8,
                    background: "#2C5F4F",
                    color: "#fff",
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontWeight: 500,
                    fontSize: 14,
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLAnchorElement).style.background = "#3A7A63")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLAnchorElement).style.background = "#2C5F4F")
                  }
                >
                  {link.label}
                </Link>
              );
            }
            return (
              <Link
                key={link.path}
                to={link.path}
                className="whitespace-nowrap cursor-pointer relative"
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 500,
                  fontSize: 14,
                  color: isActive ? "#2C5F4F" : "#1A1A1A",
                  transition: "color 0.3s",
                  paddingBottom: 2,
                  borderBottom: isActive ? "2px solid #2C5F4F" : "2px solid transparent",
                }}
              >
                {link.label}
              </Link>
            );
          })}
          <LanguageSwitcher />
        </nav>

        {/* Mobile: language switcher + hamburger */}
        <div className="md:hidden flex items-center gap-1">
          <LanguageSwitcher />
          <button
            className="flex flex-col justify-center items-center gap-1 cursor-pointer w-8 h-8"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={t("nav.menu")}
          >
            <span
              style={{
                display: "block",
                width: 22,
                height: 2,
                background: "#1A1A1A",
                borderRadius: 2,
                transition: "transform 0.2s, opacity 0.2s",
                transform: menuOpen ? "translateY(6px) rotate(45deg)" : "none",
              }}
            />
            <span
              style={{
                display: "block",
                width: 22,
                height: 2,
                background: "#1A1A1A",
                borderRadius: 2,
                transition: "opacity 0.2s",
                opacity: menuOpen ? 0 : 1,
              }}
            />
            <span
              style={{
                display: "block",
                width: 22,
                height: 2,
                background: "#1A1A1A",
                borderRadius: 2,
                transition: "transform 0.2s",
                transform: menuOpen ? "translateY(-6px) rotate(-45deg)" : "none",
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          style={{
            background: "rgba(250,245,232,0.98)",
            borderTop: "1px solid rgba(245,237,214,0.8)",
            padding: "12px 24px 20px",
          }}
          className="md:hidden flex flex-col gap-2"
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="py-3 cursor-pointer"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: 15,
                color: location.pathname === link.path ? "#2C5F4F" : "#1A1A1A",
                fontWeight: location.pathname === link.path ? 600 : 400,
                borderBottom: "1px solid #f5f5f5",
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
